// src/components/Sidebar.tsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MemoryModal from './MemoryModal';
import ModelsModal from './ModelsModal';
import { Theme } from '../types';
import {
  Conversation,
  BillingSource,
  UserTeamSummary,
  isTeamBilled,
  teamUsableCredits,
  teamCreditsBreakdown,
} from '../types';
import { SharedConversation, ProjectRow, ProjectVisibility } from './chat/teamService';
import { layoutStyles } from '../styles/layout.styles';

// Radius scale, app-wide: 6 (micro chips) / 8 (controls, rows) / 12 (cards,
// menus) / 99 (pills). The sidebar used to carry 4, 5, 7, 9 and 10 as well.
//
// The bespoke hover-tooltip div that used to live here is gone: the sidebar had
// exactly one of them ("Close sidebar") while every other control in the app
// uses the native `title`, so two tooltip mechanisms existed to serve one label.

interface SidebarUserInfo {
  status: string;
  name: string;
  email: string;
  credits_used: number;
  // Team-aware credit fields from /auth/credit-info (see src/types.ts User).
  credits_total?: number | null;
  billing_source?: BillingSource;
  team?: UserTeamSummary | null;
  raw_status?: string;
  has_team?: boolean;
}

interface SidebarProps {
  theme: Theme;
  isSidebarOpen: boolean;
  setIsSidebarOpen: (open: boolean) => void;
  isMobile: boolean;
  themeColors: any;
  handleNewChat: () => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isLoadingConversations: boolean;
  conversationPage: number;
  filteredConversations: Conversation[];
  isFetchingMessages: boolean;
  currentConversationId: number | null;
  hoveredConversationId: number | null;
  setHoveredConversationId: (id: number | null) => void;
  handleConversationSelect: (id: number, uuid: string) => void;
  editingTitleId: number | null;
  editingTitle: string;
  setEditingTitle: (title: string) => void;
  handleTitleUpdate: (id: number) => void;
  setEditingTitleId: (id: number | null) => void;
  startEditing: (e: React.MouseEvent, conversation: Conversation) => void;
  deleteConversation: (id: number) => void;
  hasMoreConversations: boolean;
  userInfo?: SidebarUserInfo | null;
  setTheme?: (theme: Theme) => void;
  // Conversations whose run finished while not on screen - jade "result ready"
  // badge until opened. (Never shown for the open conversation.)
  unseenDoneIds?: Set<number>;
  // Conversations shared with the user's team (Teams phase 2). Rendered as a
  // SEPARATE "Tim" block below the personal list - never merged into
  // filteredConversations, whose grouping uses one flat cursor and keys by
  // conversation.id (which can collide across scopes). Absent/empty for
  // team-less users, which hides the section entirely.
  sharedConversations?: SharedConversation[];
  // Projects visible to the user (Teams phase 3). Rendered as a sub-list ABOVE
  // the shared conversations INSIDE the same "Tim" block, with its own key
  // prefix (`proj-`) so it can never collide with conversation ids. Absent or
  // empty simply hides the sub-list.
  projects?: ProjectRow[];
  // The UNFILTERED personal list. `filteredConversations` is already narrowed
  // by the search box, which would hide a project's own chats whenever the
  // query matched the PROJECT NAME instead of a title. Nesting therefore reads
  // from this one and applies the project search rule itself. Absent falls
  // back to the filtered list (same behaviour as before the one-home rework).
  allConversations?: Conversation[];
  // True when the account belongs to a team. It is what lets the "Dibagikan"
  // block render its empty state ("no shared chats yet") instead of
  // disappearing, which is the only way a new member ever learns the Bagikan
  // button exists. It also gates the "Tim · {name}" row and the scope control
  // on the inline project create.
  hasTeam?: boolean;

  // --- Nav rework (2026-07-27) ---
  // Which project rows are open. Owned by the shell (persisted + auto-opened
  // for the project you are standing in); the sidebar only reports clicks.
  expandedProjects?: Set<number>;
  onToggleProject?: (projectId: number) => void;
  // The single create path, shared with the top bar's "Project baru…" row.
  // Resolves to the created row, or null when it failed.
  onCreateProject?: (
    name: string,
    visibility?: ProjectVisibility
  ) => Promise<ProjectRow | null>;
  // Team accounts pick Tim/Privat when creating; a team-less one only ever
  // makes personal projects, so the control is hidden rather than lying.
  canScopeProjects?: boolean;
  // "Chat baru di project ini": the row's [+] and the empty-state row. Runs the
  // full new-chat reset in the shell and lands on /c?project=:id.
  onNewChatInProject?: (projectId: number) => void;
  // The project whose PANEL is open (/p/:id), highlighted in the list.
  activeProjectId?: number | null;
  teamName?: string | null;
  onLogout?: () => void;
  // Which surface the shell is showing, so the spine can mark "Tim" as current.
  surface?: 'chat' | 'team' | 'project';
}

// Server timestamps are naive UTC (no timezone suffix); parsing them as-is
// would treat them as local time and skew everything by the UTC offset
// (e.g. fresh messages showing "7 jam lalu" in WIB). Append 'Z' when no
// timezone info is present so they parse as the UTC instants they are.
export const parseServerDate = (dateStr: string): Date => {
  const hasTz = /(?:Z|[+-]\d{2}:?\d{2})$/.test(dateStr);
  return new Date(hasTz ? dateStr : dateStr.replace(' ', 'T') + 'Z');
};

// Bucket a conversation's timestamp into one of the Indonesian date groups.
// Returns null when there's no parseable date, letting the caller fall back
// to an ungrouped list.
const dateGroupLabel = (dateStr?: string): string | null => {
  if (!dateStr) return null;
  const d = parseServerDate(dateStr);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dayMs = 86400000;
  const t = d.getTime();
  if (t >= startOfToday) return 'Hari ini';
  if (t >= startOfToday - dayMs) return 'Kemarin';
  if (t >= startOfToday - 7 * dayMs) return '7 hari terakhir';
  return 'Lebih lama';
};

// Compact Indonesian relative time for the row's second line.
// Returns null when no parseable date exists so the line can be omitted.
// Exported so the project page renders its conversation rows with the exact
// same wording as the sidebar.
export const relTime = (dateStr?: string): string | null => {
  if (!dateStr) return null;
  const d = parseServerDate(dateStr);
  if (isNaN(d.getTime())) return null;
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 60000) return 'Baru saja';
  const min = Math.floor(diffMs / 60000);
  if (min < 60) return `${min} menit lalu`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} jam lalu`;
  const days = Math.floor(hr / 24);
  if (days === 1) return 'Kemarin';
  if (days < 7) return `${days} hari lalu`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks} minggu lalu`;
  return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' });
};

const Sidebar: React.FC<SidebarProps> = ({
  theme,
  isSidebarOpen,
  setIsSidebarOpen,
  isMobile,
  themeColors,
  handleNewChat,
  searchQuery,
  setSearchQuery,
  isLoadingConversations,
  conversationPage,
  filteredConversations,
  isFetchingMessages,
  currentConversationId,
  hoveredConversationId,
  setHoveredConversationId,
  handleConversationSelect,
  editingTitleId,
  editingTitle,
  setEditingTitle,
  handleTitleUpdate,
  setEditingTitleId,
  startEditing,
  deleteConversation,
  hasMoreConversations,
  userInfo,
  setTheme,
  unseenDoneIds,
  sharedConversations,
  projects,
  allConversations,
  hasTeam,
  expandedProjects,
  onToggleProject,
  onCreateProject,
  onNewChatInProject,
  canScopeProjects,
  activeProjectId,
  teamName,
  onLogout,
  surface = 'chat',
}) => {
  const c = themeColors[theme];
  const navigate = useNavigate();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const createInputRef = useRef<HTMLInputElement>(null);

  // Inline project create (the [+] in the PROJECT header). A draft row at the
  // top of the list: Enter creates, Esc cancels, no navigation.
  const [creatingProject, setCreatingProject] = useState(false);
  const [draftProjectName, setDraftProjectName] = useState('');
  const [draftVisibility, setDraftVisibility] = useState<ProjectVisibility>('team');
  const [createPending, setCreatePending] = useState(false);
  const [createError, setCreateError] = useState('');

  // Hover target of the PROJECT rows (the gear fades in with it).
  const [hoveredProjectId, setHoveredProjectId] = useState<number | null>(null);

  // Inline delete confirm on a conversation row: the × becomes "Yakin hapus?"
  // with a "Batal" beside it. Replaces window.confirm, which was the last
  // browser dialog left in the shell.
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  // Touch widths collapse rename+delete into ONE 36px overflow control; this is
  // the row whose actions are currently expanded.
  const [openActionsId, setOpenActionsId] = useState<number | null>(null);

  // Account menu on the user card (P1-1: the card LOOKED like an entry and was
  // inert). Anchored above the card because it sits at the bottom of the rail.
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [memoryModalOpen, setMemoryModalOpen] = useState(false);
  const [modelsModalOpen, setModelsModalOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!accountMenuOpen) return;
    const onDown = (event: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [accountMenuOpen]);

  const accountItemStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    background: 'none',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 9px',
    fontSize: '12.5px',
    fontFamily: 'inherit',
    color: themeColors[theme].ink,
    cursor: 'pointer',
  };

  // One-time hint under the PROJECT header, same "seen" pattern as the team
  // welcome card. Existing users have muscle memory for a row that used to
  // navigate; this line is what tells them it now expands.
  const HINT_KEY = 'powerai_project_hint_seen';
  const [hintDismissed, setHintDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(HINT_KEY) === '1';
    } catch {
      return true;
    }
  });
  const dismissHint = () => {
    setHintDismissed(true);
    try {
      localStorage.setItem(HINT_KEY, '1');
    } catch {
      /* storage disabled - the hint simply comes back next session */
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobile && isSidebarOpen) {
        const sidebarElement = document.querySelector('[data-sidebar]');
        if (sidebarElement && !sidebarElement.contains(event.target as Node)) {
          setIsSidebarOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile, isSidebarOpen, setIsSidebarOpen]);

  // ⌘K / Ctrl+K focuses the conversation search.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Compute footer identity from userInfo (email prefix per spec).
  const emailPrefix = userInfo?.email ? userInfo.email.split('@')[0] : '';
  const displayName = emailPrefix || userInfo?.name || 'Anda';
  const avatarLetter = (displayName[0] || 'A').toUpperCase();
  // Credits line. When a TEAM wallet pays (the monthly jatah or the top-up
  // pool, whichever the backend picked), the shared number replaces the
  // personal "remaining" count and the plan slot reads TIM. billing_source
  // absent (backend without the team fields) keeps the old behaviour exactly.
  const isTeamPool = isTeamBilled(userInfo?.billing_source);
  // 10000 = USER_LIMITS['active'] under pricing v3 (Rp199.000 / 10.000 kredit).
  // It is only a fallback for a backend that has not shipped credits_total;
  // the server number always wins.
  const legacyTotal =
    userInfo?.status === 'lite'
      ? 0
      : userInfo?.status === 'active' || userInfo?.status === 'limited'
        ? 10000
        : 0;
  const totalCredits =
    userInfo?.billing_source === 'none'
      ? 0
      : userInfo?.billing_source === 'personal'
        ? userInfo?.credits_total ?? 10000
        : legacyTotal;
  const remaining = Math.max(0, totalCredits - (userInfo?.credits_used ?? 0));
  // A CAPPED member is governed by their OWN monthly cap, not by the shared
  // wallets (P2-2): showing the team number told them credits they cannot
  // spend, while their real allowance sat two clicks deep. Uncapped members
  // (and the owner) have no personal number to count down, so they get the one
  // honest usable figure instead: jatah bulanan + kantong top up, the same
  // number the top bar shows. The title breaks it into its two wallets.
  const memberLimit = userInfo?.team?.member_monthly_limit ?? null;
  const memberUsed = userInfo?.team?.member_monthly_used ?? 0;
  const memberHasCap = isTeamPool && memberLimit != null;
  const teamCredits = teamUsableCredits(userInfo?.team);
  const creditsText = isTeamPool
    ? memberHasCap
      ? Math.max(0, Math.round((memberLimit as number) - memberUsed)).toLocaleString('id-ID')
      : teamCredits.toLocaleString('id-ID')
    : remaining.toLocaleString('id-ID');
  const creditsUnit = isTeamPool
    ? memberHasCap ? 'kredit sisa' : 'kredit tim'
    : 'kredit';
  const creditsTitle = isTeamPool
    ? memberHasCap
      ? `Batas bulananmu: ${Math.round(memberUsed).toLocaleString('id-ID')} dari ${Math.round(memberLimit as number).toLocaleString('id-ID')} kredit terpakai`
      : `Kredit tim: ${teamCreditsBreakdown(userInfo?.team)}`
    : `${(userInfo?.credits_used ?? 0).toLocaleString('id-ID')} dari ${totalCredits.toLocaleString('id-ID')} kredit terpakai`;
  const planLabel = isTeamPool
    ? 'TIM'
    : userInfo?.status === 'active' || userInfo?.status === 'limited'
      ? 'PRO'
      : (userInfo?.status || 'FREE').toUpperCase();

  const showSkeleton = isLoadingConversations && conversationPage === 1;

  // ---------------------------------------------------------------------------
  // ONE CHAT, ONE HOME (UX iteration 2026-07-27)
  //
  // Every conversation appears in EXACTLY ONE place in this sidebar:
  //
  //   under its project   if it has a project_id we can resolve to a visible
  //                       project. Own rows come from the PERSONAL list (any
  //                       visibility, so a private chat inside a project shows
  //                       up too, which the shared list can never report);
  //                       other people's come from the shared list.
  //   "Dibagikan"         other people's shared chats that are in no project.
  //   date groups         own chats that are in no project. Nothing else.
  //
  // Two data sources describe the same conversation whenever the viewer shared
  // their own chat, so nesting dedupes by conversation id and PREFERS the
  // personal-list row: that is the one the run lifecycle keeps fresh (the
  // shared list only moves on a refetch), and it carries the live
  // active_run_status stamp.
  //
  // Rows without owner_email (older backend) are treated as somebody else's:
  // we cannot prove they are ours, and dropping them would hide real team rows.
  // ---------------------------------------------------------------------------
  const myEmail = (userInfo?.email || '').toLowerCase();
  const teamQuery = searchQuery.trim().toLowerCase();
  const allShared = sharedConversations || [];
  const personalSource = allConversations || filteredConversations;
  const matchesQuery = (row: SharedConversation) =>
    !teamQuery || (row.title || '').toLowerCase().includes(teamQuery);
  const isOwnRow = (row: SharedConversation) => {
    const owner = (row.owner_email || '').toLowerCase();
    return !!owner && !!myEmail && owner === myEmail;
  };

  const knownProjectIds = new Set((projects || []).map((p) => p.id));
  const inVisibleProject = (pid?: number | null) =>
    pid !== null && pid !== undefined && knownProjectIds.has(pid);

  // A personal row rendered inside a project, in the shared-row shape. Keeping
  // the original Conversation alongside it is what lets the nested row keep the
  // rename and delete affordances the date-grouped row has.
  interface NestedRow {
    row: SharedConversation;
    own?: Conversation;
  }

  const asSharedRow = (cv: Conversation): SharedConversation => ({
    id: cv.id,
    uuid: cv.uuid,
    title: cv.title,
    owner_email: userInfo?.email || '',
    project_id: cv.project_id ?? null,
    updated_at: cv.updated_at || cv.created_at,
    active_run_status: cv.active_run_status ?? null,
  });

  // project id -> the rows filed in it, personal source first so the dedupe
  // below keeps the fresher row.
  const nestedByProject = new Map<number, NestedRow[]>();
  const push = (pid: number, entry: NestedRow) => {
    const bucket = nestedByProject.get(pid);
    if (bucket) bucket.push(entry);
    else nestedByProject.set(pid, [entry]);
  };
  personalSource.forEach((cv) => {
    if (inVisibleProject(cv.project_id)) {
      push(cv.project_id as number, { row: asSharedRow(cv), own: cv });
    }
  });
  const looseShared: SharedConversation[] = [];
  allShared.forEach((row) => {
    // A project_id we cannot resolve to a visible project (someone filed a
    // team-shared conversation in a project we cannot see) falls back to the
    // flat list rather than vanishing.
    if (!inVisibleProject(row.project_id)) {
      looseShared.push(row);
      return;
    }
    push(row.project_id as number, { row });
  });
  // Dedupe by conversation id, first entry wins (personal source is pushed
  // first), then newest activity first like every other list here.
  nestedByProject.forEach((entries, pid) => {
    const seen = new Set<number>();
    const unique = entries.filter((e) => {
      if (seen.has(e.row.id)) return false;
      seen.add(e.row.id);
      return true;
    });
    unique.sort((a, b) => {
      const ta = a.row.updated_at ? parseServerDate(a.row.updated_at).getTime() : 0;
      const tb = b.row.updated_at ? parseServerDate(b.row.updated_at).getTime() : 0;
      return tb - ta;
    });
    nestedByProject.set(pid, unique);
  });

  // Nested rows for a project. When the search matches the project NAME the
  // whole project opens up; otherwise only the conversations that match.
  const nestedRowsFor = (project: ProjectRow): NestedRow[] => {
    const entries = nestedByProject.get(project.id) || [];
    if (!teamQuery) return entries;
    if ((project.name || '').toLowerCase().includes(teamQuery)) return entries;
    return entries.filter((e) => matchesQuery(e.row));
  };
  // A project stays visible while its name OR any conversation inside it
  // matches the search.
  const projectRows = (projects || []).filter(
    (p) =>
      !teamQuery ||
      (p.name || '').toLowerCase().includes(teamQuery) ||
      nestedRowsFor(p).length > 0
  );
  const looseRows = looseShared.filter((row) => !isOwnRow(row) && matchesQuery(row));

  // The date groups keep ONLY the conversations that have no home elsewhere.
  // An unresolvable project_id (projects still loading, or a project that was
  // deleted) deliberately falls back here: a chat must never vanish.
  const datedConversations = filteredConversations.filter(
    (cv) => !inVisibleProject(cv.project_id)
  );

  // The Tim block renders whenever the account has a team, so a member with
  // nothing shared yet gets the hint instead of an absent section. Older
  // callers that do not pass hasTeam keep the previous "content or nothing"
  // behaviour.
  const showTeamBlock =
    projectRows.length > 0 || looseRows.length > 0 || !!hasTeam;
  // "Nothing at all to show": the personal groups are empty AND the team block
  // has nothing either. Only then is the bare empty line the right answer.
  const showEmptyState =
    datedConversations.length === 0 && projectRows.length === 0 && looseRows.length === 0;

  // ONE shared-conversation row idiom, used both nested under a project and
  // flat under "Dibagikan": running spinner, owner label ("kamu" for the
  // viewer's own), relative time, single-line truncation.
  //
  // `own` is set for a row that came from the PERSONAL list (the viewer's own
  // chat, nested under its project). It keeps the rename + delete affordances
  // there, so moving a chat into a project never costs the user those actions.
  const dangerColor = theme === 'light' ? '#DC2626' : '#F87171';

  // The gear (and the row action cluster) is a hover affordance on a mouse, but
  // hover does not exist on touch and a first-time user has no reason to hunt
  // for it, so it stays visible on mobile and until the one-time hint is gone.
  const gearAlwaysVisible = isMobile || !hintDismissed;

  // Width reserved on EVERY conversation row for the action cluster. The
  // cluster is absolutely positioned inside that reserve, so a row with actions
  // and a row without truncate their titles at exactly the same x - which the
  // old in-flow buttons could never do (each row ended wherever its own buttons
  // began). Sized to the cluster that actually renders: desktop shows the
  // rename+delete PAIR (2x24px + gap + right inset ~= 60px; the old 46px let
  // the pencil sit on the title tail), mobile shows one 36px overflow control.
  const ACTION_SLOT = isMobile ? '46px' : '62px';

  // Rename + delete for a row the viewer OWNS.
  //  - desktop: the two glyph buttons, revealed on hover
  //  - touch (<=768px): ONE 36px "⋯" overflow control that expands the pair,
  //    since two 24px targets were both under the 36px floor. Visibility
  //    follows the same gearAlwaysVisible rule the project gear uses.
  //  - delete asks inline ("Yakin hapus?" / "Batal"), never window.confirm.
  const renderRowActions = (conversation: Conversation, isHover: boolean) => {
    const confirming = confirmDeleteId === conversation.id;
    const expanded = openActionsId === conversation.id;
    // Desktop: hover-only. The old rule borrowed gearAlwaysVisible (tied to the
    // one-time PROJECT hint), which kept pencil+delete stamped on every row for
    // new users - visually noisy and crowding the titles. The project gear
    // keeps its own hint-driven visibility; row actions do not.
    const visible = isHover || isMobile || confirming || expanded;
    const showPair = !isMobile || expanded;
    const touchBox: React.CSSProperties = isMobile
      ? { minWidth: '36px', minHeight: '36px' }
      : { minWidth: '24px', minHeight: '24px' };
    const glyphBtn = (color: string): React.CSSProperties => ({
      ...touchBox,
      display: 'grid',
      placeItems: 'center',
      background: 'none',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      padding: '2px 4px',
      color,
      fontFamily: 'inherit',
      ['--pa-focus-ring' as any]: c.jade,
      ['--pa-focus-ring-dim' as any]: c.jadeDim,
    });
    const wordBtn = (color: string): React.CSSProperties => ({
      ...touchBox,
      display: 'inline-flex',
      alignItems: 'center',
      background: 'none',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      padding: '0 8px',
      fontSize: '11.5px',
      fontWeight: 600,
      whiteSpace: 'nowrap',
      color,
      fontFamily: 'inherit',
      ['--pa-focus-ring' as any]: c.jade,
      ['--pa-focus-ring-dim' as any]: c.jadeDim,
    });
    return (
      <div
        className="pa-reveal"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          right: '6px',
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          opacity: visible ? 1 : 0,
          pointerEvents: visible ? 'auto' : 'none',
          transition: 'opacity 150ms ease',
          // The confirm pair is wider than the reserve and sits over the title,
          // so it needs its own surface to stay readable.
          ...(confirming
            ? {
                // Fully opaque (same surface as the account menu): at 0.98 the
                // row title bled through the question.
                background: theme === 'dark' ? '#141619' : '#FFFFFF',
                border: `1px solid ${c.line2}`,
                borderRadius: '8px',
                padding: '2px',
                boxShadow: c.shadowSoft,
              }
            : null),
        }}
      >
        {confirming ? (
          <>
            <button
              type="button"
              className="pa-focusable"
              autoFocus
              onClick={(e) => {
                e.stopPropagation();
                setConfirmDeleteId(null);
                setOpenActionsId(null);
                deleteConversation(conversation.id);
              }}
              title="Hapus percakapan ini. Tindakan ini tidak bisa dibatalkan."
              style={wordBtn(dangerColor)}
            >
              Yakin hapus?
            </button>
            <button
              type="button"
              className="pa-focusable"
              onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
              style={wordBtn(c.ink2)}
            >
              Batal
            </button>
          </>
        ) : showPair ? (
          <>
            <button
              type="button"
              className="pa-focusable"
              onClick={(e) => { e.stopPropagation(); startEditing(e, conversation); }}
              title="Ubah judul"
              aria-label="Ubah judul"
              style={glyphBtn(c.ink3)}
            >
              <span style={{ fontSize: '13px' }}>✎</span>
            </button>
            <button
              type="button"
              className="pa-focusable pa-danger-hover"
              onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(conversation.id); }}
              title="Hapus"
              aria-label="Hapus percakapan"
              style={{ ...glyphBtn(c.ink3), ['--pa-danger' as any]: dangerColor }}
            >
              <span style={{ fontSize: '15px' }}>×</span>
            </button>
          </>
        ) : (
          <button
            type="button"
            className="pa-focusable"
            onClick={(e) => { e.stopPropagation(); setOpenActionsId(conversation.id); }}
            title="Tindakan lain"
            aria-label="Tindakan lain"
            aria-haspopup="true"
            aria-expanded={false}
            style={glyphBtn(c.ink3)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <circle cx="5" cy="12" r="1.6" />
              <circle cx="12" cy="12" r="1.6" />
              <circle cx="19" cy="12" r="1.6" />
            </svg>
          </button>
        )}
      </div>
    );
  };

  const renderSharedRow = (row: SharedConversation, key: string, own?: Conversation) => {
    const isActive = currentConversationId === row.id;
    const isHover = hoveredConversationId === row.id;
    const isRunning = row.active_run_status === 'running';
    const timeLabel = relTime(row.updated_at);
    const ownerEmail = row.owner_email || '';
    const isMine = !!own || isOwnRow(row);
    const ownerLabel = isMine ? 'kamu' : (ownerEmail.split('@')[0] || 'anggota tim');
    const isEditing = !!own && editingTitleId === row.id;
    const openRow = () => {
      if (isFetchingMessages || isEditing) return;
      if (own) handleConversationSelect(own.id, own.uuid);
      else navigate(`/c/${row.uuid}`);
      if (window.innerWidth <= 768) setIsSidebarOpen(false);
    };
    return (
      <div
        key={key}
        className="conversation-item pa-focusable"
        role="button"
        tabIndex={isFetchingMessages ? -1 : 0}
        onClick={openRow}
        onKeyDown={(e) => {
          if (isEditing) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openRow();
          }
        }}
        onMouseEnter={() => !isFetchingMessages && setHoveredConversationId(row.id)}
        onMouseLeave={() => setHoveredConversationId(null)}
        title={
          own
            ? (row.title || 'Chat baru')
            : (ownerEmail ? `Dibagikan oleh ${ownerEmail}` : 'Dibagikan ke tim')
        }
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          // Right inset is the reserved action slot (see ACTION_SLOT).
          padding: `7px ${ACTION_SLOT} 7px 10px`,
          borderRadius: '8px',
          marginBottom: '2px',
          cursor: isFetchingMessages ? 'not-allowed' : 'pointer',
          background: isActive ? c.panel2 : (isHover && !isFetchingMessages ? c.panel : 'transparent'),
          opacity: isFetchingMessages ? 0.5 : 1,
          transition: 'background-color 150ms ease',
          ['--pa-focus-ring' as any]: c.jade,
          ['--pa-focus-ring-dim' as any]: c.jadeDim,
        }}
      >
        {/* Same 13px gutter the project rows use: the active dot lives in it,
            so the title never shifts and every row in the Tim block lines up. */}
        <span style={{ width: '13px', flexShrink: 0, display: 'grid', placeItems: 'center' }}>
          {isActive && (
            <span style={{
              width: '4px',
              height: '4px',
              borderRadius: '50%',
              background: c.jade,
            }} />
          )}
        </span>
        {isEditing ? (
          <input
            value={editingTitle}
            onChange={(e) => setEditingTitle(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') handleTitleUpdate(row.id);
              else if (e.key === 'Escape') setEditingTitleId(null);
            }}
            onClick={(e) => e.stopPropagation()}
            onBlur={() => handleTitleUpdate(row.id)}
            className="pa-field"
            style={{
              flex: 1,
              minWidth: 0,
              padding: '3px 6px',
              border: `1px solid ${c.line2}`,
              borderRadius: '6px',
              background: c.bg,
              color: c.ink,
              fontSize: '13px',
              outline: 'none',
            }}
            autoFocus
          />
        ) : (
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
            <span style={{
              fontSize: '13px',
              fontWeight: 500,
              color: (isActive || isHover) ? c.ink : c.ink2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              transition: 'color 150ms ease',
            }}>
              {row.title || 'Tanpa judul'}
            </span>
            {/* Own chat shared to the team: a quiet glyph, because the row
                stays HERE (DIBAGIKAN lists what OTHERS share with you) and
                without a mark its shared-ness was invisible in the sidebar. */}
            {(row as { visibility?: string }).visibility === 'team' && (
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={c.jade} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.85 }} aria-label="Dibagikan ke tim">
                <circle cx="9" cy="7" r="3.2" />
                <path d="M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6" />
                <path d="M16.5 4.6a3.2 3.2 0 0 1 0 4.8" />
                <path d="M18.5 13.4c1.9.9 3 2.6 3 4.6" />
              </svg>
            )}
          </div>
          {isRunning ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              fontSize: '11px',
              color: c.jade,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              <span style={{
                width: '10px',
                height: '10px',
                border: `2px solid ${c.line2}`,
                borderTopColor: c.jade,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                flexShrink: 0,
              }} />
              <span>{ownerLabel} · Sedang berjalan…</span>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              fontSize: '11px',
              color: c.ink2,
              // No active-only indent here: the dot lives in the fixed 13px
              // gutter above, so the meta line already lines up with the title
              // and the old paddingLeft just made the row twitch on select.
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              <span>{timeLabel ? `${ownerLabel} · ${timeLabel}` : ownerLabel}</span>
            </div>
          )}
        </div>
        )}

        {/* Rename + delete, own rows only. Same cluster as the date-grouped
            list, so a chat filed in a project keeps every action it had before
            it was moved there. */}
        {own && !isEditing && renderRowActions(own, isHover)}
      </div>
    );
  };

  const railBtnStyle: React.CSSProperties = {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    display: 'grid',
    placeItems: 'center',
    background: 'none',
    border: 'none',
    color: c.ink3,
    cursor: 'pointer',
    padding: 0,
    ['--pa-focus-ring' as any]: c.jade,
    ['--pa-focus-ring-dim' as any]: c.jadeDim,
  };

  // Section label of the spine ("PROJECT" / "CHAT" / "DIBAGIKAN"): a scope
  // header, not a date bucket, so it gets brighter ink and wider tracking.
  const sectionLabelStyle: React.CSSProperties = {
    fontWeight: 600,
    fontSize: '10px',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: c.ink2,
  };

  const submitCreateProject = async () => {
    const name = draftProjectName.trim();
    if (!name || createPending || !onCreateProject) return;
    setCreatePending(true);
    setCreateError('');
    try {
      const created = await onCreateProject(
        name,
        canScopeProjects ? draftVisibility : undefined
      );
      if (created?.id != null) {
        setCreatingProject(false);
        setDraftProjectName('');
      } else {
        setCreateError('Gagal membuat project.');
      }
    } finally {
      setCreatePending(false);
    }
  };

  const cancelCreateProject = () => {
    setCreatingProject(false);
    setDraftProjectName('');
    setCreateError('');
  };

  // One glyph for the rail and the project rows.
  const folderGlyph = (size: number, color: string) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  );

  const teamGlyph = (size: number, color: string) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );

  // Collapsed desktop rail. Without it the closed sidebar loses every project
  // and team entry point (P2-1) and the only way back is to reopen the sidebar.
  const renderRail = () => (
    <div
      data-sidebar-rail
      style={{
        width: '52px',
        minWidth: '52px',
        height: '100vh',
        // Above the composer's fixed gradient strip, which spans the full width
        // when the sidebar is closed and otherwise swallowed the rail's lower
        // half (the account button was unclickable).
        position: 'relative',
        zIndex: 1000,
        borderRight: `1px solid ${c.line}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '6px',
        padding: '14px 0 12px',
        background: 'transparent',
      }}
    >
      {/* All five rail glyphs are the SAME 32x32 box on the SAME radius with the
          same 6px pitch: the logo used to be 30px/r9 and the avatar 28px/r8,
          which read as three different button sizes down one 52px column. */}
      <button
        type="button"
        onClick={() => setIsSidebarOpen(true)}
        title="Buka sidebar"
        aria-label="Buka sidebar"
        className="pa-focusable"
        style={{
          ...railBtnStyle,
          background: c.markBg,
          border: `1px solid ${c.line2}`,
          boxShadow: c.markGlow,
        }}
      >
        <img src={c.logoSrc} alt="Power AI" style={{ width: '15px', height: '15px', objectFit: 'contain' }} />
      </button>

      <button
        type="button"
        onClick={handleNewChat}
        title="Chat baru"
        aria-label="Chat baru"
        className="pa-icon-ghost"
        style={railBtnStyle}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
          <path d="M12 5v14M5 12h14" />
        </svg>
      </button>

      <button
        type="button"
        onClick={() => setIsSidebarOpen(true)}
        title="Project"
        aria-label="Project"
        className="pa-icon-ghost"
        style={railBtnStyle}
      >
        {folderGlyph(16, 'currentColor')}
      </button>

      {hasTeam && (
        <button
          type="button"
          onClick={() => navigate('/team')}
          title={teamName ? `Tim ${teamName}` : 'Tim'}
          aria-label={teamName ? `Tim ${teamName}` : 'Tim'}
          className="pa-icon-ghost"
          style={{
            ...railBtnStyle,
            color: surface === 'team' ? c.jade : c.ink3,
          }}
        >
          {teamGlyph(16, 'currentColor')}
        </button>
      )}

      <button
        type="button"
        onClick={() => setIsSidebarOpen(true)}
        title={`Akun ${displayName}`}
        aria-label={`Akun ${displayName}`}
        className="pa-focusable"
        style={{
          ...railBtnStyle,
          marginTop: 'auto',
          background: c.panel2,
          border: `1px solid ${c.line2}`,
          fontWeight: 600,
          fontSize: '11.5px',
          color: c.ink2,
        }}
      >
        {avatarLetter}
      </button>
    </div>
  );

  return (
    <>
      {/* Dim scrim behind the mobile drawer (click-through closes via the
          existing outside-click handler) */}
      {isMobile && isSidebarOpen && (
        <div
          onClick={() => setIsSidebarOpen(false)}
          aria-hidden="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 999,
            // Direct handler: the old design relied on a document-level
            // mousedown listener, which iOS does not reliably synthesize for
            // a handler-less div - taps on the scrim could do nothing.
            cursor: 'pointer',
          }}
        />
      )}

      {/* Collapsed desktop rail: project + team stay reachable with the sidebar
          closed instead of disappearing entirely. */}
      {!isMobile && !isSidebarOpen && renderRail()}

    <div
      data-sidebar
      style={{
        ...layoutStyles.sidebar,
        display: isSidebarOpen ? 'flex' : 'none',
        position: isMobile ? 'fixed' : 'relative',
        flexDirection: 'column' as const,
        gap: 0,
        borderRight: `1px solid ${c.line}`,
        top: 0,
        left: 0,
        height: '100vh',
        zIndex: 1000,
        // Mobile drawer: near-opaque surface so the thread never bleeds
        // through; desktop stays transparent over the app glow.
        backgroundColor: isMobile
          ? (theme === 'dark' ? 'rgba(10,11,13,0.96)' : 'rgba(247,248,250,0.96)')
          : 'transparent',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        transition: 'all 0.3s ease-in-out',
        padding: '16px 12px 12px',
        overflow: 'hidden',
        width: '280px',
        minWidth: '280px',
        // Interaction-state vars consumed by .pa-field / .pa-focusable /
        // .pa-icon-ghost in index.css, so every focus ring in the spine is jade
        // in BOTH themes instead of falling back to the dark-theme default.
        ['--pa-focus-ring' as any]: c.jade,
        ['--pa-focus-ring-dim' as any]: c.jadeDim,
      }}
    >
      {/* Brand */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        // 10px left so the mark sits on the SAME x as every row title below it
        // (project names, chat titles, "Tim", the user card); 6px right, the
        // same inset the trailing row icons use.
        padding: '2px 6px 16px 10px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
          <span style={{
            width: '24px',
            height: '24px',
            borderRadius: '8px',
            background: c.markBg,
            border: `1px solid ${c.line2}`,
            display: 'grid',
            placeItems: 'center',
            boxShadow: c.markGlow,
          }}>
            {/* Real Power AI logo (wireframe polyhedron); chip + asset flip with theme. */}
            <img
              src={c.logoSrc}
              alt="Power AI"
              style={{ width: '15px', height: '15px', objectFit: 'contain' }}
            />
          </span>
          <b style={{ fontWeight: 650, fontSize: '14px', letterSpacing: '-0.01em', color: c.ink }}>Power AI</b>
        </div>

        {/* Native title, like every other control in the app: the bespoke
            tooltip div existed for this one English string. */}
        <button
          onClick={() => setIsSidebarOpen(false)}
          title="Tutup sidebar"
          aria-label="Tutup sidebar"
          className="pa-icon-ghost pa-touch-36"
          style={{
            display: 'grid',
            placeItems: 'center',
            width: '28px',
            height: '28px',
            borderRadius: '8px',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: c.ink3,
            ['--pa-focus-ring' as any]: c.jade,
            ['--pa-focus-ring-dim' as any]: c.jadeDim,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M9 3v18" />
          </svg>
        </button>
      </div>

      {/* New chat - quiet panel button (founder: the light primary was too loud) */}
      <button
        onClick={handleNewChat}
        className="pa-focusable"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '7px',
          height: '36px',
          borderRadius: '8px',
          background: c.panel2,
          color: c.ink,
          border: `1px solid ${c.line2}`,
          fontWeight: 600,
          fontSize: '13px',
          cursor: 'pointer',
          transition: 'background 0.15s ease, border-color 0.15s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = c.hoverBackground;
          e.currentTarget.style.borderColor = c.jade;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = c.panel2;
          e.currentTarget.style.borderColor = c.line2;
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
          <path d="M12 5v14M5 12h14" />
        </svg>
        Chat baru
      </button>

      {/* Search pill with ⌘K chip. .pa-field so typing in it lights the same
          jade border + ring the panels' inputs use (it had no focus state at
          all: the input's own outline is suppressed). */}
      <div className="pa-field" style={{
        margin: '10px 0 14px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        height: '34px',
        padding: '0 11px',
        borderRadius: '8px',
        background: c.panel2,
        border: `1px solid ${c.line2}`,
      }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={c.ink3} strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          ref={searchInputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Cari…"
          style={{
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: c.ink,
            width: '100%',
            fontSize: '12.5px',
          }}
        />
        <span style={{
          marginLeft: 'auto',
          fontFamily: c.mono,
          fontWeight: 500,
          fontSize: '10px',
          border: `1px solid ${c.line2}`,
          borderRadius: '6px',
          padding: '1px 5px',
          color: c.ink3,
          flexShrink: 0,
        }}>⌘K</span>
      </div>

      {/* Spine: PROJECT, then CHAT date groups, then DIBAGIKAN */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        paddingBottom: '8px',
        position: 'relative',
      }} className="custom-scrollbar">
        {/* PROJECT - above the date groups on purpose (P1-5): a container the
            user files things into must not sit below every chat they ever had.
            Rows expand IN PLACE; the gear is what opens the project panel. */}
        <div style={{ marginBottom: '6px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '0 6px 7px 10px',
          }}>
            <span style={sectionLabelStyle}>Project</span>
            <button
              type="button"
              onClick={() => {
                setCreateError('');
                setCreatingProject(true);
                setDraftProjectName('');
                requestAnimationFrame(() => createInputRef.current?.focus());
              }}
              title="Buat project baru"
              aria-label="Buat project baru"
              className="pa-icon-ghost pa-touch-36"
              style={{
                marginLeft: 'auto',
                width: '26px',
                height: '26px',
                borderRadius: '6px',
                display: 'grid',
                placeItems: 'center',
                background: 'none',
                border: 'none',
                color: c.ink3,
                cursor: 'pointer',
                padding: 0,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </button>
          </div>

          {/* One-time hint: the row used to navigate, so existing users need to
              be told once that it now opens in place. */}
          {!hintDismissed && projectRows.length > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '6px',
              padding: '0 8px 8px 10px',
              fontSize: '11px',
              lineHeight: 1.55,
              color: c.ink3,
            }}>
              <span style={{ flex: 1 }}>
                Klik nama project untuk buka isinya. Ikon di kanan untuk pengaturan project.
              </span>
              <button
                type="button"
                onClick={dismissHint}
                title="Mengerti"
                aria-label="Tutup petunjuk"
                style={{
                  background: 'none',
                  border: 'none',
                  color: c.ink3,
                  cursor: 'pointer',
                  fontSize: '13px',
                  lineHeight: 1,
                  padding: '0 2px',
                  flexShrink: 0,
                }}
              >
                ×
              </button>
            </div>
          )}

          {/* Inline create draft row, at the TOP of the list where the new
              project will appear. Enter creates, Esc cancels, no navigation. */}
          {creatingProject && (
            <div style={{
              padding: '6px 8px 8px 10px',
            }}>
              <input
                ref={createInputRef}
                value={draftProjectName}
                onChange={(e) => setDraftProjectName(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') void submitCreateProject();
                  else if (e.key === 'Escape') cancelCreateProject();
                }}
                onBlur={() => { if (!draftProjectName.trim() && !createPending) cancelCreateProject(); }}
                placeholder="Nama project"
                aria-label="Nama project"
                disabled={createPending}
                maxLength={80}
                className="pa-field"
                style={{
                  width: '100%',
                  padding: '7px 9px',
                  border: `1px solid ${c.line2}`,
                  borderRadius: '8px',
                  background: c.panel2,
                  color: c.ink,
                  fontSize: '13px',
                  outline: 'none',
                  fontFamily: 'inherit',
                }}
              />
              {canScopeProjects && (
                <div style={{ display: 'flex', gap: '4px', marginTop: '6px' }}>
                  {(['team', 'private'] as ProjectVisibility[]).map((v) => (
                    <button
                      key={v}
                      type="button"
                      className="pa-focusable"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setDraftVisibility(v)}
                      style={{
                        flex: 1,
                        padding: '5px 8px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: 600,
                        fontFamily: 'inherit',
                        cursor: 'pointer',
                        background: draftVisibility === v ? c.panel2 : 'transparent',
                        border: `1px solid ${draftVisibility === v ? c.jade : c.line2}`,
                        color: draftVisibility === v ? c.ink : c.ink3,
                      }}
                    >
                      {/* Sentence case, matching the assign-menu draft in the
                          top bar. The uppercase variant read as a third scope
                          vocabulary next to "Tim"/"Privat" everywhere else. */}
                      {v === 'team' ? 'Tim' : 'Privat'}
                    </button>
                  ))}
                </div>
              )}
              {createError && !createPending ? (
                // An error must not look like the neutral keyboard hint it used
                // to share a line with.
                <div style={{
                  marginTop: '6px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '5px',
                  fontSize: '10.5px',
                  lineHeight: 1.5,
                  color: dangerColor,
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" style={{ flexShrink: 0, marginTop: '1px' }}>
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 8v5M12 16.5v.01" />
                  </svg>
                  <span>{createError}</span>
                </div>
              ) : (
                <div style={{ marginTop: '6px', fontSize: '10.5px', color: c.ink3 }}>
                  {createPending ? 'Membuat…' : 'Enter untuk buat, Esc batal'}
                </div>
              )}
            </div>
          )}

          {projectRows.length === 0 && !creatingProject ? (
            <div style={{
              padding: '2px 10px 8px',
              fontSize: '11.5px',
              lineHeight: 1.6,
              color: c.ink3,
            }}>
              {teamQuery
                ? 'Tidak ada project yang cocok.'
                : 'Belum ada project. Tekan + untuk mengelompokkan chat dan file.'}
            </div>
          ) : (
            projectRows.map((project) => {
              const nested = nestedRowsFor(project);
              // Server count first (already viewer-filtered); the rendered rows
              // are the fallback, and while searching the visible matches are
              // the only honest number.
              const badgeCount = teamQuery
                ? nested.length
                : (project.conversation_count ?? nested.length);
              const isExpanded = teamQuery
                ? nested.length > 0 || (project.name || '').toLowerCase().includes(teamQuery)
                : !!expandedProjects?.has(project.id);
              const isHover = hoveredProjectId === project.id;
              const isActive = activeProjectId === project.id;
              return (
                <div key={`proj-${project.id}`}>
                  <div
                    onMouseEnter={() => setHoveredProjectId(project.id)}
                    onMouseLeave={() => setHoveredProjectId(null)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      // Same box as every other full-width row in the spine:
                      // x=12 on the left (the container's padding) and the same
                      // right edge, with the trailing icons inset by 6px.
                      paddingRight: '6px',
                      minHeight: isMobile ? '36px' : undefined,
                      borderRadius: '8px',
                      marginBottom: '2px',
                      background: isActive ? c.panel2 : (isHover ? c.panel : 'transparent'),
                      transition: 'background-color 150ms ease',
                    }}
                  >
                    {/* Real button: Tab and Enter work, which the old div row
                        never did (P2-3). */}
                    <button
                      type="button"
                      className="pa-focusable"
                      onClick={() => onToggleProject?.(project.id)}
                      // Name first, always: the description alone was a tooltip
                      // that never said WHICH project it belonged to.
                      title={project.description ? `${project.name} · ${project.description}` : project.name}
                      aria-expanded={isExpanded}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flex: 1,
                        minWidth: 0,
                        padding: '7px 4px 7px 10px',
                        minHeight: isMobile ? '36px' : undefined,
                        background: 'none',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        fontFamily: 'inherit',
                      }}
                    >
                      <span style={{
                        width: '13px',
                        flexShrink: 0,
                        display: 'grid',
                        placeItems: 'center',
                        color: c.ink3,
                        transform: isExpanded ? 'rotate(90deg)' : 'none',
                        transition: 'transform 150ms ease',
                      }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                          <path d="m9 18 6-6-6-6" />
                        </svg>
                      </span>
                      {folderGlyph(13, isActive ? c.jade : c.ink3)}
                      {/* A project is a CONTAINER, not a chat: 600 + full ink
                          keeps the two levels typographically distinct even
                          when a project row sits right above its own chats. */}
                      <span style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: '13px',
                        fontWeight: 600,
                        color: c.ink,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {project.name}
                      </span>
                      {badgeCount > 0 && (
                        <span
                          title={`${badgeCount.toLocaleString('id-ID')} chat`}
                          style={{
                            fontFamily: c.mono,
                            fontVariantNumeric: 'tabular-nums',
                            fontSize: '10.5px',
                            color: c.ink3,
                            flexShrink: 0,
                          }}
                        >
                          {badgeCount.toLocaleString('id-ID')}
                        </span>
                      )}
                    </button>
                    {/* "Chat baru di project ini" right where the project is:
                        an empty project had no way at all to receive a chat
                        from the spine, and its expanded state was a dead line
                        of text. */}
                    <button
                      type="button"
                      onClick={() => {
                        onNewChatInProject?.(project.id);
                        if (window.innerWidth <= 768) setIsSidebarOpen(false);
                      }}
                      title={`Chat baru di project ${project.name}`}
                      aria-label={`Chat baru di project ${project.name}`}
                      className="pa-focusable pa-touch-36 pa-reveal"
                      style={{
                        display: 'grid',
                        placeItems: 'center',
                        width: '26px',
                        height: '26px',
                        borderRadius: '6px',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        color: c.ink3,
                        cursor: 'pointer',
                        opacity: (gearAlwaysVisible || isHover || isActive) ? 1 : 0,
                        pointerEvents: (gearAlwaysVisible || isHover || isActive) ? 'auto' : 'none',
                        transition: 'opacity 150ms ease, color 150ms ease',
                        flexShrink: 0,
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = c.jade; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = c.ink3; }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                    </button>
                    {/* An <a>, so middle-click and cmd-click open the project in
                        a new tab like any other link. */}
                    <a
                      href={`/p/${project.id}`}
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                        e.preventDefault();
                        navigate(`/p/${project.id}`);
                        if (window.innerWidth <= 768) setIsSidebarOpen(false);
                      }}
                      title={`Pengaturan project ${project.name}`}
                      aria-label={`Pengaturan project ${project.name}`}
                      className="pa-focusable pa-touch-36 pa-reveal"
                      style={{
                        display: 'grid',
                        placeItems: 'center',
                        width: '26px',
                        height: '26px',
                        borderRadius: '6px',
                        color: isActive ? c.jade : c.ink3,
                        opacity: (gearAlwaysVisible || isHover || isActive) ? 1 : 0,
                        pointerEvents: (gearAlwaysVisible || isHover || isActive) ? 'auto' : 'none',
                        transition: 'opacity 150ms ease',
                        flexShrink: 0,
                      }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="3" />
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.2.6.77 1 1.41 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                      </svg>
                    </a>
                  </div>

                  {/* The project's chats, in place behind a rail so the
                      hierarchy is unmistakable. */}
                  {isExpanded && (
                    nested.length > 0 ? (
                      <div style={{
                        marginLeft: '16px',
                        paddingLeft: '4px',
                        borderLeft: `1px solid ${c.line}`,
                        marginBottom: '4px',
                      }}>
                        {nested.map((entry) =>
                          renderSharedRow(
                            entry.row,
                            `proj-${project.id}-conv-${entry.row.id}`,
                            entry.own
                          )
                        )}
                      </div>
                    ) : (
                      // An empty project used to expand into a dead sentence.
                      // The one thing a user wants there is to put a chat in it,
                      // so the empty state IS that action.
                      <div style={{
                        marginLeft: '16px',
                        paddingLeft: '4px',
                        borderLeft: `1px solid ${c.line}`,
                        marginBottom: '4px',
                      }}>
                        {/* A dashed slot, not a solid row: it reads as an empty
                            place waiting to be filled rather than as a chat
                            that is already there. */}
                        <button
                          type="button"
                          className="pa-focusable"
                          onClick={() => {
                            onNewChatInProject?.(project.id);
                            if (window.innerWidth <= 768) setIsSidebarOpen(false);
                          }}
                          title={`Chat baru di project ${project.name}`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            width: '100%',
                            padding: '7px 10px',
                            minHeight: isMobile ? '36px' : undefined,
                            borderRadius: '8px',
                            background: 'none',
                            border: `1px dashed ${c.line2}`,
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontFamily: 'inherit',
                            fontSize: '12.5px',
                            color: c.ink3,
                            transition: 'background-color 150ms ease, color 150ms ease, border-color 150ms ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = c.panel;
                            e.currentTarget.style.color = c.jade;
                            e.currentTarget.style.borderColor = c.jade;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'none';
                            e.currentTarget.style.color = c.ink3;
                            e.currentTarget.style.borderColor = c.line2;
                          }}
                        >
                          <span style={{ width: '13px', flexShrink: 0, display: 'grid', placeItems: 'center' }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                              <path d="M12 5v14M5 12h14" />
                            </svg>
                          </span>
                          Chat baru di project ini
                        </button>
                      </div>
                    )
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* CHAT - the date groups. Only chats that live in no project reach
            this list; everything else has a home above. */}
        <div style={{ ...sectionLabelStyle, padding: '10px 10px 7px' }}>Chat</div>
        {showSkeleton ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '0 2px' }}>
            {[1, 2, 3, 4, 5].map((n) => (
              <div key={n} style={{
                height: '30px',
                borderRadius: '8px',
                background: c.panel,
                animation: 'pulse 1.5s infinite',
                animationDelay: `${n * 0.12}s`,
              }} />
            ))}
          </div>
        ) : datedConversations.length === 0 ? (
          <div style={{
            padding: '10px',
            fontSize: '12px',
            color: c.ink3,
          }}>
            {teamQuery ? 'Tidak ada chat yang cocok.' : 'Belum ada chat.'}
          </div>
        ) : (
          <>
            {(() => {
              const anyDated = datedConversations.some(
                (cv) => dateGroupLabel(cv.updated_at || cv.created_at) !== null
              );
              let lastLabel: string | null = null;
              let renderedHeader = false;
              return datedConversations.map((conversation) => {
                const isActive = currentConversationId === conversation.id;
                const isHover = hoveredConversationId === conversation.id;
                const label = anyDated ? dateGroupLabel(conversation.updated_at || conversation.created_at) : null;
                const showHeader = anyDated && label !== null && label !== lastLabel;
                if (showHeader) lastLabel = label;
                const timeLabel = relTime(conversation.updated_at || conversation.created_at);
                const isCompare = conversation.context_type === 'compare';
                const isRunning = conversation.active_run_status === 'running';
                // Unseen-result badge: run finished off-screen; never on the
                // open conversation (its result is already visible).
                const isUnseenDone = !isActive && !isRunning && !!unseenDoneIds?.has(conversation.id);
                const headerMarginTop = renderedHeader ? '14px' : '2px';
                // "HARI INI" directly under the CHAT header is two labels for
                // one list: the section header already says where you are, and
                // the first group is today's by construction. Later groups
                // ("Kemarin", "7 hari terakhir") still label themselves.
                const suppressFirstToday = !renderedHeader && label === 'Hari ini';
                const renderHeader = showHeader && !suppressFirstToday;
                if (showHeader) renderedHeader = true;
                const openRow = () => {
                  if (isFetchingMessages) return;
                  handleConversationSelect(conversation.id, conversation.uuid);
                  if (window.innerWidth <= 768) setIsSidebarOpen(false);
                };
                return (
                  <React.Fragment key={conversation.id}>
                    {renderHeader && (
                      <div style={{
                        fontWeight: 600,
                        fontSize: '10px',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: c.ink3,
                        padding: '0 10px 7px',
                        marginTop: headerMarginTop,
                      }}>{label}</div>
                    )}
                    <div
                      className="conversation-item pa-focusable"
                      role="button"
                      tabIndex={isFetchingMessages ? -1 : 0}
                      title={conversation.title || 'Chat baru'}
                      onClick={openRow}
                      onKeyDown={(e) => {
                        if (editingTitleId === conversation.id) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openRow();
                        }
                      }}
                      onMouseEnter={() => !isFetchingMessages && setHoveredConversationId(conversation.id)}
                      onMouseLeave={() => setHoveredConversationId(null)}
                      style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        // Right inset is the reserved action slot, so every
                        // title in the list truncates at the same x.
                        padding: `7px ${ACTION_SLOT} 7px 10px`,
                        borderRadius: '8px',
                        marginBottom: '2px',
                        cursor: isFetchingMessages ? 'not-allowed' : 'pointer',
                        background: isActive ? c.panel2 : (isHover && !isFetchingMessages ? c.panel : 'transparent'),
                        opacity: isFetchingMessages ? 0.5 : 1,
                        transition: 'background-color 150ms ease',
                      }}
                    >
                      {/* Fixed 13px state gutter, shared with the project rows
                          and the Tim block: the dot lives IN it, so the title
                          and the meta line never shift when a row goes active
                          and every row in the spine starts on the same x. */}
                      <span style={{ width: '13px', flexShrink: 0, display: 'grid', placeItems: 'center' }}>
                        {isUnseenDone ? (
                          <span style={{
                            width: '5px',
                            height: '5px',
                            borderRadius: '50%',
                            background: c.jade,
                          }} />
                        ) : isActive ? (
                          <span style={{
                            width: '4px',
                            height: '4px',
                            borderRadius: '50%',
                            background: c.jade,
                          }} />
                        ) : null}
                      </span>
                      {editingTitleId === conversation.id ? (
                        <input
                          value={editingTitle}
                          onChange={(e) => setEditingTitle(e.target.value)}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter') handleTitleUpdate(conversation.id);
                            else if (e.key === 'Escape') setEditingTitleId(null);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onBlur={() => handleTitleUpdate(conversation.id)}
                          className="pa-field"
                          style={{
                            flex: 1,
                            minWidth: 0,
                            padding: '3px 6px',
                            border: `1px solid ${c.line2}`,
                            borderRadius: '6px',
                            background: c.bg,
                            color: c.ink,
                            fontSize: '13px',
                            outline: 'none',
                          }}
                          autoFocus
                        />
                      ) : (
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
                            <span style={{
                              fontSize: '13px',
                              fontWeight: 500,
                              color: (isActive || isHover) ? c.ink : c.ink2,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              transition: 'color 150ms ease',
                            }}>
                              {conversation.title || 'Chat baru'}
                            </span>
                          </div>
                          {/* Meta line: run state takes over the second line
                              (spinner while running, jade "result ready" when
                              finished unseen); relative time otherwise. */}
                          {isRunning ? (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                              fontSize: '11px',
                              color: c.jade,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}>
                              {/* Tiny jade spinner (frozen by the global
                                  prefers-reduced-motion guard in index.css) */}
                              <span style={{
                                width: '10px',
                                height: '10px',
                                border: `2px solid ${c.line2}`,
                                borderTopColor: c.jade,
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                                flexShrink: 0,
                              }} />
                              <span>Sedang berjalan…</span>
                            </div>
                          ) : isUnseenDone ? (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                              fontSize: '11px',
                              color: c.jade,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}>
                              <span>Selesai · hasil baru</span>
                            </div>
                          ) : (timeLabel || isCompare) && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                              fontSize: '11px',
                              color: c.ink2,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}>
                              {isCompare && <span title="Compare (multi-model)" style={{ fontSize: '10px' }}>🔀</span>}
                              {timeLabel && <span>{timeLabel}</span>}
                            </div>
                          )}
                        </div>
                      )}

                      {editingTitleId !== conversation.id &&
                        renderRowActions(conversation, isHover)}
                    </div>
                  </React.Fragment>
                );
              });
            })()}

            {hasMoreConversations && !isLoadingConversations && (
              <div id="conversations-sentinel" style={{ height: '20px', width: '100%', position: 'relative', bottom: '40px' }} />
            )}

            {isLoadingConversations && conversationPage > 1 && (
              <div style={{ padding: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', color: c.ink3 }}>
                <span style={{ width: '16px', height: '16px', border: `2px solid ${c.line2}`, borderTopColor: c.jade, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                <span style={{ fontSize: '12.5px' }}>Memuat…</span>
              </div>
            )}
          </>
        )}

        {/* DIBAGIKAN - everything the team shared that is filed in NO project.
            The viewer's own rows are left out: they already have a home in the
            date groups above. */}
        {/* While searching, an empty DIBAGIKAN would be a bare label with
            nothing under it ("no results" is not the same statement as
            "nothing shared yet"), so the whole block steps aside. */}
        {hasTeam && (looseRows.length > 0 || !teamQuery) && (
          <div style={{ marginTop: '16px', borderTop: `1px solid ${c.line}`, paddingTop: '12px' }}>
            <div style={{ ...sectionLabelStyle, padding: '0 10px 8px' }}>Dibagikan</div>
            {looseRows.length > 0 ? (
              looseRows.map((row) => renderSharedRow(row, `team-${row.id}`))
            ) : (
              !teamQuery && (
                <div style={{
                  padding: '2px 10px 4px',
                  fontSize: '11.5px',
                  lineHeight: 1.6,
                  color: c.ink3,
                }}>
                  Chat yang dibagikan anggota lain akan muncul di sini. Chat yang kamu bagikan sendiri tetap di daftar chatmu, ditandai ikon tim.
                </div>
              )
            )}
          </div>
        )}
      </div>

      {/* Team entry + account card. "Tim · {name}" is the labelled home team
          management never had in the spine (P1-1 / directive 4). */}
      <div style={{ marginTop: 'auto' }}>
        {hasTeam && (
          <button
            type="button"
            onClick={() => {
              navigate('/team');
              if (window.innerWidth <= 768) setIsSidebarOpen(false);
            }}
            title={teamName ? `Tim ${teamName}` : 'Tim'}
            className="pa-focusable"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              // Same box as "Chat baru", the project rows and the user card:
              // full bleed inside the sidebar's 12px padding, 10px inner inset.
              padding: '8px 10px',
              minHeight: '36px',
              marginBottom: '4px',
              borderRadius: '8px',
              background: surface === 'team' ? c.panel2 : 'transparent',
              border: 'none',
              cursor: 'pointer',
              textAlign: 'left',
              fontFamily: 'inherit',
            }}
            onMouseEnter={(e) => {
              if (surface !== 'team') e.currentTarget.style.background = c.panel;
            }}
            onMouseLeave={(e) => {
              if (surface !== 'team') e.currentTarget.style.background = 'transparent';
            }}
          >
            <span style={{ width: '13px', flexShrink: 0, display: 'grid', placeItems: 'center' }}>
              {teamGlyph(13, surface === 'team' ? c.jade : c.ink3)}
            </span>
            <span style={{
              flex: 1,
              minWidth: 0,
              fontSize: '13px',
              fontWeight: 500,
              color: surface === 'team' ? c.ink : c.ink2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {teamName ? `Tim · ${teamName}` : 'Tim'}
            </span>
          </button>
        )}

        {/* Teamless: the SAME row slot sells the upgrade instead of hiding it.
            The only in-app door to /team used to be a footnote on the
            subscription page; a persistent, quiet row here makes "upgrade
            jadi tim" discoverable without a banner. */}
        {!hasTeam && (
          <button
            type="button"
            onClick={() => {
              navigate('/team');
              if (window.innerWidth <= 768) setIsSidebarOpen(false);
            }}
            title="Upgrade ke plan Team: satu tagihan untuk timmu, kursi fleksibel"
            className="pa-focusable"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              padding: '8px 10px',
              minHeight: '36px',
              marginBottom: '4px',
              borderRadius: '8px',
              background: surface === 'team' ? c.panel2 : 'transparent',
              border: 'none',
              cursor: 'pointer',
              textAlign: 'left',
              fontFamily: 'inherit',
            }}
            onMouseEnter={(e) => {
              if (surface !== 'team') e.currentTarget.style.background = c.panel;
            }}
            onMouseLeave={(e) => {
              if (surface !== 'team') e.currentTarget.style.background = 'transparent';
            }}
          >
            <span style={{ width: '13px', flexShrink: 0, display: 'grid', placeItems: 'center' }}>
              {teamGlyph(13, surface === 'team' ? c.jade : c.ink3)}
            </span>
            <span style={{
              flex: 1,
              minWidth: 0,
              fontSize: '13px',
              fontWeight: 500,
              color: surface === 'team' ? c.ink : c.ink2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              Pakai bareng tim
            </span>
            <span style={{
              fontSize: '10px',
              fontFamily: c.mono,
              letterSpacing: '0.04em',
              color: c.jade,
              flexShrink: 0,
            }}>
              HEMAT
            </span>
          </button>
        )}

        {/* Footer user card */}
        <div
          ref={accountMenuRef}
          style={{
            position: 'relative',
            borderTop: `1px solid ${c.line}`,
            // Zero side padding: the row itself is the full-width box (x=12 to
            // the same right edge as every other row) and the 10px inset lives
            // on the button inside it, like the Tim row above.
            padding: '10px 0 2px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <button
            type="button"
            onClick={() => setAccountMenuOpen((v) => !v)}
            title="Akun"
            aria-label="Akun"
            aria-haspopup="menu"
            aria-expanded={accountMenuOpen}
            className="pa-focusable"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flex: 1,
              minWidth: 0,
              padding: '5px 10px',
              minHeight: '36px',
              borderRadius: '8px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              textAlign: 'left',
              fontFamily: 'inherit',
            }}
          >
            <span style={{
              width: '27px',
              height: '27px',
              borderRadius: '8px',
              background: c.panel2,
              border: `1px solid ${c.line2}`,
              display: 'grid',
              placeItems: 'center',
              fontWeight: 600,
              fontSize: '11.5px',
              color: c.ink2,
              flexShrink: 0,
            }}>{avatarLetter}</span>
            <span style={{ minWidth: 0, flex: 1 }}>
              <span style={{ display: 'block', fontWeight: 500, fontSize: '12.5px', color: c.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayName}</span>
              {/* Mono is reserved for the NUMBER; the scope word and the unit
                  are prose and stay in Inter. A CAPPED member sees their OWN
                  remaining allowance here, not the team wallets (P2-2);
                  everyone else on a team sees the usable team credits. */}
              <span
                title={creditsTitle}
                style={{ display: 'block', fontWeight: 400, fontSize: '10.5px', color: c.ink3 }}
              >
                {planLabel} ·{' '}
                <span style={{ fontFamily: c.mono, fontVariantNumeric: 'tabular-nums' }}>
                  {creditsText}
                </span>{' '}
                {creditsUnit}
              </span>
            </span>
          </button>

          {setTheme && (
            <button
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              title={theme === 'dark' ? 'Mode terang' : 'Mode gelap'}
              aria-label={theme === 'dark' ? 'Mode terang' : 'Mode gelap'}
              className="pa-icon-ghost pa-touch-36"
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '8px',
                display: 'grid',
                placeItems: 'center',
                background: 'none',
                border: 'none',
                color: c.ink3,
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              {theme === 'dark' ? (
                /* sun */
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                </svg>
              ) : (
                /* moon */
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              )}
            </button>
          )}

          {accountMenuOpen && (
            <div
              role="menu"
              style={{
                position: 'absolute',
                bottom: 'calc(100% + 6px)',
                left: 0,
                right: 0,
                background: theme === 'dark' ? '#141619' : '#FFFFFF',
                border: `1px solid ${c.line2}`,
                borderRadius: '12px',
                boxShadow: c.shadow,
                padding: '5px',
                zIndex: 1001,
              }}
            >
              <div style={{
                padding: '7px 9px 8px',
                borderBottom: `1px solid ${c.line}`,
                fontSize: '11px',
                color: c.ink3,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {userInfo?.email || displayName}
              </div>
              {hasTeam ? (
                <>
                  <button
                    role="menuitem"
                    onClick={() => { setAccountMenuOpen(false); navigate('/team'); }}
                    style={accountItemStyle}
                  >
                    Tim
                  </button>
                  <button
                    role="menuitem"
                    onClick={() => { setAccountMenuOpen(false); navigate('/team#topup'); }}
                    style={accountItemStyle}
                  >
                    Tambah kredit
                  </button>
                </>
              ) : (
                <button
                  role="menuitem"
                  onClick={() => { setAccountMenuOpen(false); navigate('/team'); }}
                  style={accountItemStyle}
                >
                  Upgrade ke Team
                </button>
              )}
              <button
                role="menuitem"
                onClick={() => { setAccountMenuOpen(false); setModelsModalOpen(true); }}
                style={accountItemStyle}
              >
                Model yang dipakai
              </button>
              <button
                role="menuitem"
                onClick={() => { setAccountMenuOpen(false); setMemoryModalOpen(true); }}
                style={accountItemStyle}
              >
                Memori
              </button>
              <button
                role="menuitem"
                onClick={() => { setAccountMenuOpen(false); onLogout?.(); }}
                style={{ ...accountItemStyle, color: theme === 'light' ? '#DC2626' : '#F87171' }}
              >
                Keluar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
    <MemoryModal
      open={memoryModalOpen}
      onClose={() => setMemoryModalOpen(false)}
      theme={theme}
      c={c}
    />
    <ModelsModal
      open={modelsModalOpen}
      onClose={() => setModelsModalOpen(false)}
      theme={theme}
      c={c}
    />
    </>
  );
};
export default Sidebar;
